import React, { useState, useEffect, useRef } from 'react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { format } from 'date-fns';

const JOB_TYPE_OPTIONS = ['Asbestos abatement','Asbestos Testing','Bad Debt','Biohazard','Board-up','Cleaning','Contents assessment','Contents cleaning','Contents relocation','Duct cleaning','Equipment Rental','Fire','Habitat for Humanity Bucks County','Mold','Mold Post Testing','Mold Pre-Testing','Odor','Other','Overhead Hours - Shop','Pack back','Pack In/Packout','Pack out','Radon mitigation','Radon testing','Recon Make Safe','Reconstruction','Referred to SP','Soot','Storage','Supplemental Fire','Supplemental Mold','Supplemental Water','Testing','United Way Bucks - Mit','United Way Bucks - Recon','Water','Water PM Sold','Water - PL bonus'];

const FilterSelect = ({ value, onChange, options, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // When the user types, narrow the visible options to those that contain
  // the typed substring (case-insensitive). When the field is empty or
  // already equals an option exactly, show every option.
  const filteredOptions = (() => {
    const v = (value || '').trim().toLowerCase();
    if (!v) return options;
    if (options.some((o) => o.toLowerCase() === v)) return options;
    return options.filter((o) => o.toLowerCase().includes(v));
  })();

  return (
    <div className="relative flex-1" ref={ref}>
      <input
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:border-[#6BADE0] focus:ring-1 focus:ring-[#6BADE0]"
        data-testid={testId}
      />
      {open && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-[#6BADE0] rounded shadow-lg max-h-48 overflow-y-auto">
          <div
            className={`px-3 py-2 text-sm cursor-pointer hover:bg-[#5B9BD5] hover:text-white ${value === '' ? 'bg-[#5B9BD5] text-white' : 'text-gray-400'}`}
            onMouseDown={(e) => { e.preventDefault(); onChange(''); setOpen(false); }}
          >
            (clear)
          </div>
          {filteredOptions.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400 italic">No matches</div>
          ) : filteredOptions.map((opt) => (
            <div
              key={opt}
              className={`px-3 py-2 text-sm cursor-pointer transition-colors ${value === opt ? 'bg-[#5B9BD5] text-white' : 'text-gray-700 hover:bg-[#5B9BD5] hover:text-white'}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(opt); setOpen(false); }}
            >
              {opt}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Multi-select variant used for Job Status so the user can filter by more
// than one status at once (e.g. "In Progress" + "Completed"). The selected
// values are stored as a comma-joined string on the filter so the existing
// stat-card quick filters (which set a single status) keep working.
const FilterMultiSelect = ({ value, onChange, options, testId }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const selected = (value || '').split(',').map((s) => s.trim()).filter(Boolean);

  const toggle = (opt) => {
    const exists = selected.some((s) => s.toLowerCase() === opt.toLowerCase());
    const next = exists
      ? selected.filter((s) => s.toLowerCase() !== opt.toLowerCase())
      : [...selected, opt];
    onChange(next.join(', '));
  };

  return (
    <div className="relative flex-1" ref={ref}>
      <div
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[38px] border border-gray-300 rounded px-3 py-1.5 text-sm cursor-pointer focus:outline-none focus:border-[#6BADE0] flex flex-wrap gap-1 items-center"
        data-testid={testId}
      >
        {selected.length === 0 ? (
          <span className="text-gray-400">&nbsp;</span>
        ) : (
          selected.map((s) => (
            <span
              key={s}
              className="inline-flex items-center gap-1 bg-[#5B9BD5] text-white rounded px-2 py-0.5 text-xs"
              data-testid={`${testId}-chip-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {s}
              <span
                className="cursor-pointer font-bold leading-none"
                onClick={(e) => { e.stopPropagation(); toggle(s); }}
              >
                ×
              </span>
            </span>
          ))
        )}
      </div>
      {open && (
        <div className="absolute z-[100] w-full mt-1 bg-white border border-[#6BADE0] rounded shadow-lg max-h-48 overflow-y-auto">
          <div
            className="px-3 py-2 text-sm cursor-pointer text-gray-400 hover:bg-[#5B9BD5] hover:text-white"
            onMouseDown={(e) => { e.preventDefault(); onChange(''); }}
          >
            (clear)
          </div>
          {options.map((opt) => {
            const isSel = selected.some((s) => s.toLowerCase() === opt.toLowerCase());
            return (
              <div
                key={opt}
                className={`px-3 py-2 text-sm cursor-pointer transition-colors flex items-center gap-2 ${isSel ? 'bg-[#5B9BD5] text-white' : 'text-gray-700 hover:bg-[#5B9BD5] hover:text-white'}`}
                onMouseDown={(e) => { e.preventDefault(); toggle(opt); }}
                data-testid={`${testId}-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <span className={`w-4 h-4 flex items-center justify-center border rounded-sm text-[10px] shrink-0 ${isSel ? 'bg-white text-[#5B9BD5] border-white' : 'border-gray-400'}`}>
                  {isSel ? '✓' : ''}
                </span>
                {opt}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DatePickerInput = ({ value, onChange, testId }) => {
  const [calOpen, setCalOpen] = useState(false);
  const parsed = value ? new Date(value) : undefined;

  const handleSelect = (date) => {
    if (date) {
      onChange(format(date, 'M/dd/yyyy'));
    } else {
      onChange('');
    }
    setCalOpen(false);
  };

  return (
    <Popover open={calOpen} onOpenChange={setCalOpen}>
      <PopoverTrigger asChild>
        <input
          type="text"
          value={value}
          readOnly
          className="border border-gray-300 rounded px-3 py-2 text-sm w-0 flex-1 min-w-0 cursor-pointer focus:outline-none focus:border-[#6BADE0] focus:ring-1 focus:ring-[#6BADE0]"
          data-testid={testId}
          onClick={() => setCalOpen(true)}
        />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={parsed} onSelect={handleSelect} initialFocus />
      </PopoverContent>
    </Popover>
  );
};

const FilterModal = ({ open, onClose, filters, setFilters, onUpdate, fields, employees, projectTypes }) => {
  if (!open) return null;

  // Keep in sync with the canonical Status `<select>` in
  // DetailInformationTab.jsx so filtering matches what the user can
  // actually set on a project. On-Hold + Scheduled used to be missing,
  // which made those rows unfilterable from the boards.
  const jobStatusOptions = ['Accounts Receivable', 'Check-In', 'Closed', 'Completed', 'Dead', 'Estimate', 'In Progress', 'On-Hold', 'Scheduled'];
  const paymentTypeOptions = ['Direct Pay', 'COI', 'Claim'];
  const paidOptions = ['No', 'Yes'];
  // Aging buckets used by the AR board's >60 / >90 stat cards. Bad-debt
  // projects are excluded from these counts in `filteredProjects`.
  const agingOptions = ['Over 60 days', 'Over 90 days'];
  const jobTypeOptions = Array.from(new Set((projectTypes || []).map((t) => t.name || t))).sort();
  const employeeNames = Array.from(
    new Set((employees || []).map((e) => `${e.last_name}, ${e.first_name}`.trim()))
  ).sort();

  const getOptions = (fieldType) => {
    switch (fieldType) {
      case 'job_status': return jobStatusOptions;
      case 'job_type': return jobTypeOptions.length > 0 ? jobTypeOptions : JOB_TYPE_OPTIONS;
      case 'payment_type': return paymentTypeOptions;
      case 'paid_in_full': return paidOptions;
      case 'aging': return agingOptions;
      case 'estimator':
      case 'office_contact':
      case 'production_mgr':
      case 'marketing_rep':
      case 'project_lead':
        return employeeNames;
      default: return [];
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-start justify-center pt-8 pb-8 z-50 overflow-y-auto" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md flex flex-col max-h-[calc(100vh-4rem)] my-auto" onClick={(e) => e.stopPropagation()} data-testid="filter-modal">
        <h2 className="text-xl text-[#5B8DB8] font-light italic px-6 pt-6 pb-4 shrink-0">Edit Filters</h2>
        <div className="space-y-4 px-6 overflow-y-auto flex-1">
          {fields.map((field) => {
            if (field.type === 'date_range') {
              return (
                <div key={field.fromKey} className="flex items-center gap-3">
                  <label className="w-32 text-sm text-gray-700 text-right shrink-0 font-medium">{field.label}:</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <DatePickerInput value={filters[field.fromKey]} onChange={(v) => setFilters({ ...filters, [field.fromKey]: v })} testId={`filter-${field.fromKey}`} />
                    <span className="text-sm text-gray-500 shrink-0">to</span>
                    <DatePickerInput value={filters[field.toKey]} onChange={(v) => setFilters({ ...filters, [field.toKey]: v })} testId={`filter-${field.toKey}`} />
                  </div>
                </div>
              );
            }
            if (field.type === 'number_range') {
              return (
                <div key={field.fromKey} className="flex items-center gap-3">
                  <label className="w-32 text-sm text-gray-700 text-right shrink-0 font-medium">{field.label}:</label>
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <input type="text" placeholder="$0" value={filters[field.fromKey] || ''} onChange={(e) => setFilters({ ...filters, [field.fromKey]: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm w-24" data-testid={`filter-${field.fromKey}`} />
                    <span className="text-sm text-gray-500 shrink-0">to</span>
                    <input type="text" placeholder="$0" value={filters[field.toKey] || ''} onChange={(e) => setFilters({ ...filters, [field.toKey]: e.target.value })} className="border border-gray-300 rounded px-2 py-1.5 text-sm w-24" data-testid={`filter-${field.toKey}`} />
                  </div>
                </div>
              );
            }
            return (
              <div key={field.key} className="flex items-center gap-3">
                <label className="w-32 text-sm text-gray-700 text-right shrink-0 font-medium">{field.label}:</label>
                {field.key === 'job_status' ? (
                  <FilterMultiSelect
                    value={filters[field.key]}
                    onChange={(v) => setFilters({ ...filters, [field.key]: v })}
                    options={getOptions(field.key)}
                    testId={`filter-${field.key}`}
                  />
                ) : (
                  <FilterSelect
                    value={filters[field.key]}
                    onChange={(v) => setFilters({ ...filters, [field.key]: v })}
                    options={getOptions(field.key)}
                    testId={`filter-${field.key}`}
                  />
                )}
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-center gap-3 px-6 py-4 mt-2 border-t border-gray-100 shrink-0 bg-white rounded-b-lg">
          <button onClick={onUpdate} className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-5 py-2 rounded text-sm font-medium transition-colors" data-testid="update-filters-btn">
            Update Filters
          </button>
          <button onClick={() => {
            const cleared = {};
            Object.keys(filters).forEach(k => { cleared[k] = ''; });
            setFilters(cleared);
            onClose();
          }} className="text-gray-600 hover:text-gray-800 px-4 py-2 text-sm transition-colors" data-testid="cancel-filters-btn">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
